import React, { useState } from "react";
import { Modal, Button } from "react-bootstrap";
import {
  useFlowController,
  useFlowData,
  Icon,
  IconColorStyle,
  IconSize,
  formatString,
  StringFormats,
  subscribeDataChange,
  unsubscribeDataChange,
  getFlowDataDirect,
  setFlowDataDirect,
  logEvent,
  updateQuoteNumberToPets,
} from "@adaptis/flow-components";
import CoveredPet from "../CoveredPet";
import { getPetQty, removePet, isPetValid } from "../../util/data-store";
import {
  CoveredPetStatus,
  CoveredPetAction,
} from "../CoveredPet/CoveredPet.types";
import { ICoveredPetGroupProps } from "./CoveredPetGroup.types";
import { PetType, Pets, IPet } from "../../util/common-types";
import { getPaymentFrequencyLabel } from "../../util/data-store";
import PetsyFeatureLabelGroup from "../PetsyFeatureLabelGroup";
import "./CoveredPetGroup.scss";
import PetsyPaymentFrequency from "../PetsyPaymentFrequency";
import Coupon from "../Coupon";
import PetsyQuoteNumber from "../PetsyQuoteNumber";
import { has, random } from "lodash";
import Config from "../../app-config";
import { getQuote } from "../../util/knose-api-wrapper";
import LoadingLightbox from "../LoadingLightbox";
import _ from "../../imports/lodash-imports";
import { getAFXProfile } from "../../util/helper-functions";
import { callApiSaveQuote } from "../../KnoseAPI/Lib/Utils";

// Helper function để xử lý timeout
function timeout(
  promise: Promise<any>,
  ms: number,
  timeoutError: symbol
): Promise<any> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(timeoutError);
    }, ms);

    promise
      .then((value) => {
        clearTimeout(timer);
        resolve(value);
      })
      .catch((reason) => {
        clearTimeout(timer);
        reject(reason);
      });
  });
}

// Helper function để tìm index của pet từ pet_no
function getPetIndexFromId(id: string, pets: any[]): number {
  return pets.findIndex((pet) => pet.id === id);
}
function getPetIndexFromPetNo(petNo: string, pets: any[]): number {
  return pets.findIndex((pet) => pet.pet_no === petNo);
}

export default function CoveredPetGroup(props: ICoveredPetGroupProps) {
  const { gotoStep } = useFlowController();
  const { getData, setData } = useFlowData();
  const [wasPrice, setWasPrice] = useState(0); //Tổng số tiền hằng năm
  const [discountedPrice, setDiscountedPrice] = useState(0); // Giá sau giảm giá tính theo năm
  const [totalDiscounted, setTotalDiscounted] = useState(0); //Tổng số tiền được giảm theo năm
  const [firstMonthPrice, setFirstMonthPrice] = useState(0); //Số tiền giảm của tháng đầu tiên

  const [paymentFrequency, setPaymentFrequency] = React.useState("");
  const paymentFrequencyValue = getData("root.selectedPaymentFrequency");
  const [loadingPrice, setLoadingPrice] = React.useState(false);
  const [loadingPriceQuote, setLoadingPriceQuote] = React.useState(false);
  const [pets, setPets] = React.useState<Pets>([]);
  const [petQty, setPetQty] = React.useState<any>(null);
  // Modal
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const [deleteModalPet, setDeleteModalPetName] = React.useState({
    petId: "",
    name: "",
  });

  const petTotalAmounts = getData("totalAmounts");

  const [isIncomplete, setIsIncomplete] = React.useState(true);
  const [hasCoupon, setHasCoupon] = React.useState(false);
  let transferQuote = getFlowDataDirect("TransferQuoteCallBack") === "Yes";
  transferQuote = false;
  // let pets: Pets = React.useMemo(() => getData(props.dataField ?? ""), []);
  // let petQty = getPetQty(pets);

  React.useEffect(() => {
    const petsData: Pets = props.dataField
      ? getData(props.dataField ?? "")
      : [];
    if (!petsData) return;
    setPets(petsData);
    setPetQty(getPetQty(petsData));
    calculatePrice(petsData);
    let promoCode = getData("root.promoCode");
    if (promoCode) setHasCoupon(true);
    else checkAndSetMultiPets(petsData);
  }, []);

  React.useEffect(() => {
    calculatePrice(pets);
  }, [petQty, paymentFrequency, pets]);

  async function checkAndSetMultiPets(pets: Pets) {
    let petId = getData("id");
    // Kiểm tra nhanh các điều kiện không cần xử lý
    if (!pets || !Array.isArray(pets) || petId || Object.keys(pets).length === 0) return;

    const multiPetPromoCode = "MULTIPET";
    const promoCode = getData("root.promoCode");

    // Thoát sớm nếu đã có promo code (giúp tránh xử lý không cần thiết)
    if (promoCode) return;

    // Lọc pets có customCover
    const validPets = pets.filter((pet: any) => pet?.customCover);

    // Thoát sớm nếu không đủ pets để xử lý
    // if (validPets.length <= 1) {
    //   // Xử lý trường hợp 1 pet có promoCode cần xóa
    //   if (validPets.length === 1 &&
    //       validPets[0].customCover &&
    //       validPets[0].customCover.promoCode === multiPetPromoCode) {
    //     const petIndex = getPetIndexFromId(validPets[0].id, pets);
    //     setData(`pets[${petIndex}].customCover.promoCode`, null);
    //     // Gọi API để cập nhật báo giá
    //     return await updateQuoteForPets([validPets[0].id]);
    //   }
    //   return;
    // }

    // Tìm pet có giá cao nhất
    const sortedPets = [...validPets].sort(
      (a, b) =>
        (b.customCover?.premiums?.totalPayableBeforeDiscount || 0) -
        (a.customCover?.premiums?.totalPayableBeforeDiscount || 0)
    );

    const highestPricePet = sortedPets[0];
    const petsToUpdate: string[] = [];
    const updates: {
      petId: string;
      index: number;
      promoCode: string | null;
    }[] = [];

    // Xử lý pet có giá cao nhất
    if (
      highestPricePet?.customCover &&
      highestPricePet.customCover?.promoCode
    ) {
      const petIndex = getPetIndexFromId(highestPricePet.id, pets);
      updates.push({
        petId: highestPricePet.id,
        index: petIndex,
        promoCode: null,
      });
      petsToUpdate.push(highestPricePet.id);
    }

    // Xử lý các pet khác
    for (const pet of validPets) {
      // Bỏ qua pet có giá cao nhất hoặc pet không có customCover
      if (pet.id === highestPricePet.id || !pet.customCover) continue;

      // Thêm MULTIPET cho pet chưa có promoCode đúng
      if (pet.customCover.promoCode !== multiPetPromoCode) {
        const petIndex = getPetIndexFromId(pet.id, pets);
        updates.push({
          petId: pet.id,
          index: petIndex,
          promoCode: multiPetPromoCode,
        });
        petsToUpdate.push(pet.id);
      }
    }

    let petsIQuotePrice = pets.filter((pet: any) => pet.isQuotePrice);
    if (petsIQuotePrice.length > 0) {
      for (const pet of petsIQuotePrice) {
        const petIndex = getPetIndexFromId(pet.id, pets);
        let isExist = updates.find((update: any) => update.petId === pet.id);
        if (!isExist) {
          updates.push({
            petId: pet.id,
            index: petIndex,
            promoCode: pet.id === highestPricePet.id ? null : multiPetPromoCode
          });
          petsToUpdate.push(pet.id);
        }
      }
    }

    // Nếu không có pet nào cần cập nhật, dừng lại
    if (updates.length === 0) return;

    // Áp dụng các cập nhật
    for (const update of updates) {
      setData(`pets[${update.index}].customCover.promoCode`, update.promoCode);
    }

    // Gọi API để cập nhật báo giá
    await updateQuoteForPets(petsToUpdate);

    // Lưu lại quote sau khi cập nhật
    const sessionId = getData("root.sessionId") ?? "";
    const quoteNumber = getData("root.petsyQuoteNumber") ?? "";
    const parentQuoteNumber = getData("root.parentQuoteNumber") ?? "";
    const landingPage = getData("root.landingPage") ?? "";
    const holderEmail = getData("root.holderEmail") ?? "";
    let adfixusID = getAFXProfile();
    if (adfixusID === "(not set)") {
      adfixusID = "";
    }

    let petsData = getData("root.pets") ?? [];

    const quotePayload = {
      sessionId: sessionId,
      quoteNumber: quoteNumber,
      parentQuoteNumber: parentQuoteNumber,
      pets: petsData,
      profile: {
        Holder_Email: holderEmail,
        Holder_First_Name: getData("root.holderFirstName") ?? "",
        Holder_Last_Name: getData("root.holderLastName") ?? "",
        Holder_DOB: getData("root.holderDOB") ?? "",
        Holder_Phone: getData("root.holderPhone") ?? "",
        Holder_Post_Code: getData("root.holderAddress.postcode") ?? "",
        Holder_State: getData("root.holderAddress.state") ?? "",
        Holder_Suburb: getData("root.holderAddress.suburb") ?? "",
        Holder_Address_Line_1: getData("root.holderAddress.line1") ?? "",
        Holder_Address_Line_2: getData("root.holderAddress.line2") ?? "",
        Session_id: sessionId,
        channel: "FiftyupQT",
      },
    };

    let quoteSaveData = await callApiSaveQuote(quotePayload);
    if (quoteSaveData && quoteSaveData.success && quoteSaveData.data) {
      await updateQuoteNumberToPets(quoteSaveData);
      setData("root.quoteNumber", quoteSaveData.data.quoteNumber);
    }
    setData(`updatePetData1`, Math.random());
  }

  // Tách riêng logic cập nhật báo giá để dễ quản lý và tái sử dụng
  async function updateQuoteForPets(petsUpdate: string[]) {
    if (!petsUpdate.length) return;

    const timeoutError = Symbol();
    const allPets = getData("root.pets") ?? [];

    // Check xem có pet nào đang có promoCode "MULTIPET" không
    const hasAnyPromo = petsUpdate.some((petId) => {
      const pet = allPets.find((p: any) => p.id === petId);
      return pet?.customCover?.promoCode === "MULTIPET";
    });

    if (hasAnyPromo) {
      setLoadingPrice(true); 
    }else{
      setLoadingPriceQuote(true) 
    }
    
    try {
      // Lấy lại danh sách pets sau khi cập nhật
      let assignedPets = await getFlowDataDirect("pets");
      const allPets = getData("root.pets");

      const result = await timeout(
        getQuote(allPets),
        Config.websevice_timeout,
        timeoutError
      );

      if (!result || !result.data || result.data.process != result.data.total) {
        const { errMsg } = result.data || {};

        if (errMsg) {
          const isPromocodeInvalid = errMsg.find((item: any) =>
            item.includes("Promo code invalid")
          );
          setData(
            "root.isInvalidPromotionCode",
            isPromocodeInvalid ? true : false
          );
        }

        throw ["Error quote result is invalid", result];
      }

      const quote = result.data.quote;
      const promoCode = getData("root.promoCode");

      // Cập nhật thông tin premiums và promo cho từng pet
      quote.pets.forEach((pet: any) => {
        const { premiums, promo } = pet;
        const petIndex = getPetIndexFromPetNo(pet.pet_no, allPets);
        if (petIndex >= 0) {
          _.assign(assignedPets[petIndex].customCover, {
            premiums: premiums,
            promo: promo ? promo : null,
          });
          setData(`updatePetData`, Math.random());
        }
        if (promoCode && promo) {
          setData(
            "root.promoCodeType",
            parseInt(promo.promoType.toString().replace("Type", ""))
          );
        }
      });

      setPets(assignedPets);
      // Cập nhật trạng thái nếu cần
      calculatePrice(assignedPets);
      setLoadingPrice(false);
      setLoadingPriceQuote(false);
    } catch (e: any) {
      if (e === timeoutError) {
        // Xử lý timeout
        logEvent(
          "error-timeout",
          true,
          "checkAndSetMultiPets() Timeout = " + Config.websevice_timeout
        );
      } else {
        // Lỗi khác
        logEvent("error", true, "CoveredPetGroup->checkAndSetMultiPets()", e);
      }
      setLoadingPriceQuote(false);
    }
  }

  function getFirstMonthDiscounted(data: any): number {
    // Tìm đối tượng có giai đoạn chứa tháng đầu tiên (from = 1)
    const firstPeriod = data.find((item: any) => item.from === 1);

    if (!firstPeriod) {
      console.error("Không tìm thấy giai đoạn nào bắt đầu từ tháng đầu tiên.");
      return -1;
    }

    // Tổng số tiền cần trả trong giai đoạn
    const { totalPayable, totalBaseAmount, from, to } = firstPeriod;

    // Tính số tiền cần trả cho tháng đầu tiên
    // return totalPayable / totalMonths;
    return totalPayable;
  }

  function calculatePrice(pets: Pets) {
    let sumWasPrice = 0;
    let sumDiscountedPrice = 0;
    let sumFirstMonthPrice = 0;
    if (!pets || !Array.isArray(pets)) return;
    pets.forEach((pet: IPet, index: number) => {
      if (pet.customCover) {
        const cover = pet.customCover;
        if (!cover) return null;
        sumWasPrice += cover.premiums?.totalPayableBeforeDiscount ?? 0;
        sumDiscountedPrice += cover.premiums?.totalPayable ?? 0;
        if (
          ["2", "type2"].includes(
            cover?.promo?.promoType?.toString().toLowerCase()
          )
        ) {
          sumFirstMonthPrice += getFirstMonthDiscounted(
            cover?.promo?.policyPremiumInstallmentPayments ?? []
          );
        } else {
          sumFirstMonthPrice += (cover.premiums?.totalPayable ?? 0) / 12;
        }
      }
    });

    setWasPrice(sumWasPrice);
    setDiscountedPrice(sumDiscountedPrice);
    setTotalDiscounted(sumWasPrice - sumDiscountedPrice);
    setFirstMonthPrice(sumFirstMonthPrice);

    if (sumWasPrice > 0) setIsIncomplete(false);
    else setIsIncomplete(true);
  }

  function modalClose() {
    setShowDeleteModal(false);
  }

  function modalRemovePet() {
    setShowDeleteModal(false);
    let updatedPets = removePet(pets, deleteModalPet.petId);
    setData(props.dataField ?? "", updatedPets);
    setFlowDataDirect("petQty", updatedPets.length);
    let paymentFrequencyValueNew = getData("root.selectedPaymentFrequency");
    if (!paymentFrequencyValueNew)
      setData("root.selectedPaymentFrequency", "Monthly");
    else setData("root.selectedPaymentFrequency", paymentFrequencyValueNew);
    checkAndSetMultiPets(updatedPets);
  }

  function getTitle(pet: IPet) {
    switch (pet.petType) {
      case PetType.Dog:
        if (petQty.dogQty > 1) return `Your ${pet.petOrdinalName} dog`;
        else return "Your dog";
      case PetType.Cat:
        if (petQty.catQty > 1) return `Your ${pet.petOrdinalName} cat`;
        else return "Your cat";
      default:
        return "";
    }
  }

  async function onActionClick(
    action: CoveredPetAction,
    petId: string,
    name?: string
  ) {
    let urlParam: string = petId;

    switch (action) {
      case CoveredPetAction.Edit:
        let pet = pets.find((pet) => pet.id === petId);
        if (pet) {
          let petStatus = getPetStatus(pet);
          if (petStatus !== CoveredPetStatus.Configured) {
            await setData("root.quote-slug", "");
          }
        }
        gotoStep(props.editPetSlug ?? "", petId);

        setTimeout(() => {
          if (pet) pet.isTouched = true;
        }, 500);
        return;
      case CoveredPetAction.Delete:
        let adjName = name ?? "";
        if (name?.slice(0, 4) === "Your") adjName = adjName.toLowerCase();
        setDeleteModalPetName({ petId: petId, name: adjName });
        setShowDeleteModal(true);
        return;
    }
  }

  function getPetStatus(pet: IPet): CoveredPetStatus {
    pet.isValid = isPetValid(pet);

    if (pet.isValid) return CoveredPetStatus.Configured;
    else if (!pet.isTouched) return CoveredPetStatus.Untouched;
    else return CoveredPetStatus.Incomplete;
  }

  function getCalculatedPriceByMonthly() {
    let returnPrice = firstMonthPrice; // Cộng vì firstMonthDiscounted là số âm
    return returnPrice < 0 ? 0 : returnPrice;
  }

  function getCalculatedPrice(pFrequency: string) {
    if (pFrequency == "Monthly") return getCalculatedPriceByMonthly();
    else if (pFrequency == "Fortnightly")
      return (getCalculatedPriceByMonthly() * 12) / 26.07;
    else return discountedPrice;
  }

  function getProductSummaryAmount() {
    let pFrequency = paymentFrequency ?? paymentFrequencyValue;
    if (pFrequency == undefined) return;
    return (
      <div className="price-wrapper">
        <div className={`price ${hasCoupon ? "green" : ""}`}>
          {`${formatString(
            getCalculatedPrice(pFrequency),
            StringFormats.Currency
          )}`}
        </div>
        <div className="message-frequency">
          per {getPaymentFrequencyLabel(pFrequency.toLowerCase())}
        </div>
      </div>
    );
  }

  React.useEffect(() => {
    setPaymentFrequency(paymentFrequencyValue);
    // Anything in here is fired on component mount.
    subscribeDataChange(
      "SelectedPaymentFrequencyCoveredPet",
      handlePaymentFrequencyOptionChangeCoveredPet
    );

    return () => {
      // Anything in here is fired on component unmount.
      unsubscribeDataChange("SelectedPaymentFrequencyCoveredPet");
    };
  }, [paymentFrequency]);

  function handlePaymentFrequencyOptionChangeCoveredPet(
    dataPath: string,
    dataField: string,
    data: any
  ) {
    if (dataField === "selectedPaymentFrequency") {
      setPaymentFrequency(data);
    }
  }

  return (
    <div
      data-testid="CoveredPetGroup"
      className={
        "flc_covered-pet-group " +
        (petQty && petQty.total > 1 ? "multi-pets" : "") +
        (isIncomplete ? " incomplete" : "")
      }
    >
      {pets && wasPrice > 0 && (
        <PetsyPaymentFrequency
          showCoupon={true}
          autoScrollUp={true}
          stepSlug={""}
        />
      )}

      <Coupon
        isRender={false}
        fixIsNoRender={true}
        updateQuote={true}
        showCloseButton={true}
        couponDataField={"promoCode"}
        stepSlug={""}
        className="mb-3 border-description"
      />

      {pets && wasPrice > 0 && (
        <PetsyQuoteNumber
          showSaveMyQuote={true}
          className="my-5"
          showQuoteBox={true}
          textLabel={`Save My Quote`}
        />
      )}

      {pets && wasPrice > 0 && (
        <PetsyFeatureLabelGroup helperText={props.helperText} />
      )}

      {pets &&
        pets.map((pet) => (
          <CoveredPet
            className="mt-3"
            key={pet.id}
            id={pet.id}
            step={props.step}
            title={pet.name === "" ? getTitle(pet) : pet.name}
            animal={pet.petType}
            status={getPetStatus(pet)}
            summaryMode={props.summaryMode}
            summaryItems={props.summaryItems}
            onActionClick={onActionClick}
            paymentFrequency={paymentFrequency ?? "Monthly"}
            petAgeCategory={pet.petAgeCategory}
          />
        ))}
      {pets && pets.length > 0 && (
        <>
          <div className="flc_covered-pet-total">
            <div className="your-total-wrapper ">YOUR TOTAL</div>
            {getProductSummaryAmount()}
          </div>
          <div
            className={`add-pet my-4 text-style-1 ${
              petQty.total >= 5 ? "disabled" : ""
            }`}
          >
            <a
              onClick={(e) => {
                e.preventDefault();
                if (petQty.total >= 5) return;
                gotoStep(props.addPetSlug ?? "");
              }}
            >
              Add another pet
              <Icon
                className="ms-1"
                src="images/icons/plus.svg"
                colorStyle={IconColorStyle.Custom}
                size={IconSize.Custom}
              />
            </a>
          </div>
        </>
      )}
      {transferQuote && (
        <div>
          <br />
        </div>
      )}
      {pets && pets.length === 0 && (
        <div className="d-grid gap-3 my-4">
          <Button
            className="add-pet-btn"
            variant="outline-primary"
            onClick={(e) => {
              e.preventDefault();
              gotoStep(props.addPetSlug ?? "");
            }}
          >
            Add your pet
          </Button>
        </div>
      )}
      <Modal
        className="flc_covered-pet-group-modal"
        show={showDeleteModal}
        onHide={modalClose}
        backdrop="static"
        keyboard={false}
        fullscreen="sm-down"
        centered
      >
        <Modal.Header closeButton></Modal.Header>
        <Modal.Body className="text-center">
          <div>
            <Icon
              className="mb-4"
              src="images/icons/alert-triangle.svg"
              colorStyle={IconColorStyle.Warning}
              size={IconSize.ExtraLarge}
            />
          </div>
          <div className="modal-text my-3 px-5">
            Are you sure you want to remove {deleteModalPet.name} from this
            quote?
          </div>
        </Modal.Body>
        <div className="d-grid gap-3 m-3">
          <Button
            className="modal-button"
            variant="outline-dark"
            onClick={modalClose}
          >
            No, cancel
          </Button>
          <Button
            className="modal-button"
            variant="danger"
            onClick={modalRemovePet}
          >
            Yes
          </Button>
        </div>
      </Modal>
      <LoadingLightbox
        show={loadingPrice}
        title="Applying multiple pet discount"
      />
      <LoadingLightbox show={loadingPriceQuote} title="Loading your Quote..." />
    </div>
  );
}
